package com.siriusxm.pia.views.unifiedaggregator

import com.siriusxm.pia.aggregatorAdmin
import unifiedcontent.aggregator.AggregatedPermissions
import unifiedcontent.aggregator.ServiceType
import unifiedcontent.aggregator.ServiceUserMembershipList
import unifiedcontent.aggregator.ServiceUserRole
import contentingestion.unifiedmodel.EntityType

/**
 * The model that holds user roles, permissions, and service membership information
 */
class UserRoles(val app: AggregatorApplication) {
    private var serviceRoles: ServiceUserMembershipList = emptyList()
    private var aggregatedPermissions: AggregatedPermissions? = null

    suspend fun load() {
        try {
            val permissions = app.aggregator.getUserPermissions()
            serviceRoles = permissions.memberships
            aggregatedPermissions = permissions.permissions
        } catch (_: Exception) {
            // Fall back to the older roles endpoint if permissions is unavailable
            serviceRoles = app.aggregator.getUserRoles()
            aggregatedPermissions = null
        }
    }

    /**
     * Get the list of memberships of this user.
     */
    fun serviceMemberships(): ServiceUserMembershipList {
        return serviceRoles
    }

    /**
     * Get the aggregated permissions across all the user's memberships.
     */
    fun permissions(): AggregatedPermissions? {
        return aggregatedPermissions
    }

    /**
     * Returns true if the user is an ADMIN of any producer or consumer service.
     */
    fun isAnyAdmin(): Boolean {
        return adminServiceIds().isNotEmpty()
    }

    /**
     * Returns the service IDs for which the user has an ADMIN role.
     */
    fun adminServiceIds(): List<String> {
        return serviceRoles.filter { membership ->
            membership.user.roles?.contains(ServiceUserRole.ADMIN) == true
        }.map { it.serviceId }
    }

    /**
     * Determines if the current user is authorized to edit entities of the given type.
     * Returns true if the user is an admin, or if they are a member of a producer
     * that is allowed to publish this entity type.
     */
    fun isAuthorizedToEdit(entityType: EntityType): Boolean {
        if (app.context.viewer.aggregatorAdmin) {
            return true
        }
        val aggregatorService = app.currentAggregatorService() ?: return false
        val userProducerIds = serviceRoles
            .filter { it.type == ServiceType.PRODUCER }
            .map { it.serviceId }
            .toSet()
        val entityProducers = aggregatorService.producers.filter {
            it.allowedTypes?.contains(entityType.name) == true
        }
        return entityProducers.any { userProducerIds.contains(it.id) }
    }
}

